/**
 * BidNotesIntake
 * --------------
 * The second front door on the Bid Desk: paste the customer's email or meeting
 * notes, attach whatever cutsheets, drawings and floor plans came with it, and
 * AskIsaac builds the engineering brief, the load calculations and a draft
 * technical response.
 */
import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Loader2, NotebookPen, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";
import SiteSurveyPanel from "@/components/admin/SiteSurveyPanel";

export const BidNotesIntake = ({ open, onClose, token, onCreated }) => {
  const [showSurvey, setShowSurvey] = useState(false);
  const fileRef = useRef(null);
  const [title, setTitle] = useState("");
  const [customer, setCustomer] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    setFiles((f) => [...f, ...incoming.filter((n) => !f.some((x) => x.name === n.name))]);
  };

  const submit = async () => {
    if (!notes.trim()) {
      toast.error("Paste the customer's notes or email first");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("notes", notes);
      fd.append("title", title);
      fd.append("customer", customer);
      files.forEach((f) => fd.append("files", f));
      const r = await axios.post(`${API}/bid-desk/notes`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      toast.success("Notes received — AskIsaac is reading them with the attachments", {
        description: "The engineering brief, load calcs and draft letter take about a minute.",
      });
      onCreated?.(r.data.note);
      setTitle(""); setCustomer(""); setNotes(""); setFiles([]);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the analysis");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid="bid-notes-intake">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-black text-slate-900 inline-flex items-center gap-2">
              <NotebookPen className="w-5 h-5 text-indigo-600" /> Build a quote from notes
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              No bid packet needed — paste the request and attach the cutsheets, drawings and
              floor plans. Every extracted fact is tagged with where it came from.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => setShowSurvey((v) => !v)} data-testid="bid-notes-site-survey-btn">
              <Camera className="w-3.5 h-3.5 mr-1" /> {showSurvey ? "Hide site survey" : "Site survey"}
            </Button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          {showSurvey && (
            <div data-testid="bid-notes-site-survey">
              <SiteSurveyPanel scope="all" linkType="standalone" token={token}
                label="this quote" />
              <p className="text-[11px] text-slate-400 mt-1.5">
                Walk the site now — attach the survey to the bid once this quote is created.
              </p>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Project title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="LBJ OR Sterilizer — Centralized RO Filtration"
                data-testid="bid-notes-title" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Customer / facility</label>
              <Input value={customer} onChange={(e) => setCustomer(e.target.value)}
                placeholder="Harris Health System — LBJ Hospital"
                data-testid="bid-notes-customer" />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              Customer notes / email
            </label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={12}
              placeholder="Paste the email exactly as it came in — criteria, quantities, questions and all."
              className="font-mono text-xs" data-testid="bid-notes-text" />
          </div>

          <div>
            <div className="flex items-center justify-between gap-2">
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                Attachments — cutsheets, drawings, floor plans
              </label>
              <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]"
                onClick={() => fileRef.current?.click()} data-testid="bid-notes-add-files">
                <Paperclip className="w-3 h-3 mr-1" /> Add files
              </Button>
            </div>
            <input ref={fileRef} type="file" multiple accept="application/pdf,image/*"
              className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              data-testid="bid-notes-file-input" />
            {files.length ? (
              <ul className="mt-2 space-y-1">
                {files.map((f) => (
                  <li key={f.name} className="flex items-center justify-between gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                    <span className="truncate">{f.name}</span>
                    <button onClick={() => setFiles((x) => x.filter((y) => y.name !== f.name))}
                      className="text-slate-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[11px] text-slate-400 mt-1.5">
                PDFs and images, up to 40 MB each. Drawings are read page by page.
              </p>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy}
            className="bg-indigo-600 hover:bg-indigo-700" data-testid="bid-notes-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <NotebookPen className="w-3.5 h-3.5 mr-1" />}
            Analyse and draft response
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BidNotesIntake;
